import { useEffect, useState, useMemo, useCallback } from "react";
import { BADGES, PILLAR_META, getUserBadges, type Badge, type BadgePillar } from "@/lib/badges";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getUserProgress, UserProgress } from "@/lib/gamification";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { BadgeIcon } from "./BadgeIcon";
import { motion } from "framer-motion";

interface BadgeGridProps {
  userId: string;
}

interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  earned_at: string;
  seen: boolean;
}

const PILLAR_FILTERS: Array<{ key: BadgePillar | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'starting', label: 'Starting' },
  { key: 'awareness', label: 'Awareness' },
  { key: 'consistency', label: 'Consistency' },
  { key: 'recovery', label: 'Recovery' },
  { key: 'depth', label: 'Depth' },
];

export const BadgeGrid = ({ userId }: BadgeGridProps) => {
  const { t } = useLanguage();
  const [earnedBadges, setEarnedBadges] = useState<Map<string, string>>(new Map()); // badge_id -> earned_at
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePillar, setActivePillar] = useState<BadgePillar | 'all'>('all');
  const navigate = useNavigate();

  useEffect(() => {
    const abortController = new AbortController();
    const loadBadges = async () => {
      if (abortController.signal.aborted) return;
      try {
        const [badges, userProgress] = await Promise.all([
          getUserBadges(userId),
          getUserProgress(userId),
        ]);
        if (abortController.signal.aborted) return;
        const map = new Map<string, string>();
        badges.forEach((b: UserBadge) => map.set(b.badge_id, b.earned_at));
        setEarnedBadges(map);
        setProgress(userProgress);
        setError(null);
      } catch {
        if (abortController.signal.aborted) return;
        setError('Failed to load badges. Please try again.');
      } finally {
        if (!abortController.signal.aborted) setLoading(false);
      }
    };
    loadBadges();
    return () => abortController.abort();
  }, [userId]);

  const getProgressPercentage = useCallback((badge: Badge): number => {
    if (!progress) return 0;
    switch (badge.id) {
      case 'first_expense': return progress.total_expenses_logged > 0 ? 100 : 0;
      case 'seven_days': return Math.min(100, (Math.max(progress.current_streak, progress.longest_streak) / 7) * 100);
      case 'thirty_days': return Math.min(100, (Math.max(progress.current_streak, progress.longest_streak) / 30) * 100);
      case 'ninety_days': return Math.min(100, (Math.max(progress.current_streak, progress.longest_streak) / 90) * 100);
      case 'longest_streak': return progress.current_streak >= 3 ? 100 : Math.min(100, (progress.current_streak / 3) * 100);
      case 'xp_collector': return Math.min(100, (progress.xp / 5000) * 100);
      case 'coffee_lover': return Math.min(100, ((progress.coffee_count ?? 0) / 10) * 100);
      case 'pattern_spotter': return Math.min(100, ((progress.max_merchant_count ?? 0) / 5) * 100);
      case 'subscription_hunter': return Math.min(100, ((progress.recurring_count ?? 0) / 3) * 100);
      default: return 0;
    }
  }, [progress]);

  const getProgressHint = useCallback((badge: Badge): string => {
    if (!progress) return t('badges.completeToUnlock');
    switch (badge.id) {
      case 'first_expense': return t('badges.logFirstExpense');
      case 'seven_days': return t('badges.streakDays', { current: Math.max(progress.current_streak, progress.longest_streak), target: 7 });
      case 'thirty_days': return t('badges.streakDays', { current: Math.max(progress.current_streak, progress.longest_streak), target: 30 });
      case 'ninety_days': return t('badges.streakDays', { current: Math.max(progress.current_streak, progress.longest_streak), target: 90 });
      case 'xp_collector': return t('badges.xpProgress', { current: progress.xp, target: 5000 });
      case 'coffee_lover': return `${progress.coffee_count ?? 0}/10 coffees`;
      case 'pattern_spotter': return `${progress.max_merchant_count ?? 0}/5 at one merchant`;
      case 'subscription_hunter': return `${progress.recurring_count ?? 0}/3 recurring`;
      default: return t('badges.completeToUnlock');
    }
  }, [progress, t]);

  const allBadges = useMemo(() => {
    if (!progress) return [];
    return Object.values(BADGES).map(badge => {
      const isEarned = earnedBadges.has(badge.id);
      const progressPct = isEarned ? 100 : getProgressPercentage(badge);
      return { badge, isEarned, progressPct, earnedAt: earnedBadges.get(badge.id) };
    });
  }, [earnedBadges, progress, getProgressPercentage]);

  const filteredBadges = useMemo(() => {
    if (activePillar === 'all') return allBadges;
    return allBadges.filter(b => b.badge.pillar === activePillar);
  }, [allBadges, activePillar]);

  const { earned, nextWin, inProgress: inProgressList, comingSoon } = useMemo(() => {
    const e = filteredBadges.filter(b => b.isEarned);
    const notEarned = filteredBadges.filter(b => !b.isEarned);
    const withProgress = notEarned.filter(b => b.progressPct > 0).sort((a, b) => b.progressPct - a.progressPct);
    const nw = withProgress.slice(0, 2);
    const ip = withProgress.slice(2);
    const cs = notEarned.filter(b => b.progressPct === 0);
    return { earned: e, nextWin: nw, inProgress: ip, comingSoon: cs };
  }, [filteredBadges]);

  // Track next-up impressions
  useEffect(() => {
    if (nextWin.length > 0) {
      trackEvent({ name: 'next_up_viewed', props: { badge_ids: nextWin.map(b => b.badge.id) } });
    }
  }, [nextWin.length]);

  if (loading) return <div className="text-center py-8 text-muted-foreground">{t('badges.loading')}</div>;
  if (error) return (
    <div className="text-center py-8">
      <p className="text-destructive mb-4">{error}</p>
      <Button onClick={() => window.location.reload()} variant="outline">{t('common.retry')}</Button>
    </div>
  );

  const ProgressRing = ({ percent, size = 56 }: { percent: number; size?: number }) => {
    const r = (size / 2) - 3;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (percent / 100) * circumference;
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return (
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={3}
          className="stroke-muted/30" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={3}
          strokeDasharray={circumference} strokeLinecap="round"
          className="stroke-primary"
          initial={{ strokeDashoffset: prefersReducedMotion ? offset : circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, ease: "easeOut" }}
          style={{ willChange: 'stroke-dashoffset' }}
        />
      </svg>
    );
  };

  const PillarPill = ({ pillar }: { pillar: BadgePillar }) => {
    const meta = PILLAR_META[pillar];
    return (
      <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium", meta.color, meta.textColor)}>
        {meta.label}
      </span>
    );
  };

  const BadgeCard = ({ badge, isEarned, progressPct, earnedAt, highlight = false }: {
    badge: Badge; isEarned: boolean; progressPct: number; earnedAt?: string; highlight?: boolean;
  }) => (
    <Card className={cn(
      "transition-all",
      isEarned ? 'bg-card border-l-2 border-l-green-500/50' : '',
      !isEarned && progressPct > 0 ? 'bg-card/60' : '',
      !isEarned && progressPct === 0 ? 'bg-muted/20' : '',
      highlight && 'shadow-md ring-1 ring-primary/20',
    )}>
      <CardContent className="p-4 text-center space-y-2">
        <div className="relative w-14 h-14 mx-auto">
          {!isEarned && progressPct > 0 && <ProgressRing percent={progressPct} size={56} />}
          <div className="relative z-10 flex items-center justify-center h-full">
            <BadgeIcon iconName={badge.iconName} size="lg" showBackground={false}
              isLocked={!isEarned && progressPct === 0}
              className={cn(!isEarned && progressPct > 0 && 'opacity-60', !isEarned && progressPct === 0 && 'opacity-30')}
            />
          </div>
        </div>
        <div>
          <h3 className="font-medium text-sm">{t(`badges.${badge.id}.name`) !== `badges.${badge.id}.name` ? t(`badges.${badge.id}.name`) : badge.name}</h3>
          <PillarPill pillar={badge.pillar} />
          {isEarned && earnedAt && (
            <p className="text-[10px] text-muted-foreground mt-1">
              {new Date(earnedAt).toLocaleDateString()}
            </p>
          )}
          {!isEarned && progressPct > 0 && (
            <p className="text-xs text-muted-foreground mt-1">{getProgressHint(badge)}</p>
          )}
          {!isEarned && progressPct === 0 && (
            <p className="text-[10px] text-muted-foreground mt-1.5 italic">
              {t(`badges.${badge.id}.description`) !== `badges.${badge.id}.description` ? t(`badges.${badge.id}.description`) : badge.description}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Pillar filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {PILLAR_FILTERS.map(f => (
          <button key={f.key} onClick={() => setActivePillar(f.key)}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors min-h-[36px]",
              activePillar === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
            )}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Earned */}
      {earned.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">{t('badges.earned')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {earned.map(b => <BadgeCard key={b.badge.id} {...b} />)}
          </div>
        </div>
      )}

      {/* Your Next Win */}
      {nextWin.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-1">🎯 {t('badges.nextUp') || "Your Next Win"}</h2>
          <p className="text-xs text-muted-foreground mb-3">You're this close 👀</p>
          <div className="grid grid-cols-2 gap-3">
            {nextWin.map(b => <BadgeCard key={b.badge.id} {...b} highlight />)}
          </div>
        </div>
      )}

      {/* In Progress */}
      {inProgressList.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">{t('badges.inProgress')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {inProgressList.map(b => <BadgeCard key={b.badge.id} {...b} />)}
          </div>
        </div>
      )}

      {/* Coming Soon */}
      {comingSoon.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">✨ Coming Soon</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {comingSoon.map(b => <BadgeCard key={b.badge.id} {...b} />)}
          </div>
        </div>
      )}

      {/* Empty state */}
      {earned.length === 0 && nextWin.length === 0 && inProgressList.length === 0 && comingSoon.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-xl font-semibold mb-2">{t('badges.readyToEarn')}</h3>
          <p className="text-muted-foreground mb-4">{t('badges.startLogging')}</p>
          <Button onClick={() => navigate('/')}>{t('badges.logFirstExpense')}</Button>
        </div>
      )}
    </div>
  );
};
